/**
 * Owner-only banner prompting acceptance of the processor Data Processing
 * Agreement (PDPL). Renders nothing unless the signed-in user is the org owner
 * AND the current DPA version hasn't been accepted. Mounted in AppShell so it
 * appears across the dashboard apps until accepted.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getDpaStatus, acceptDpa } from '../lib/api'
import DataProcessingAgreement from './legal/DataProcessingAgreement'

export default function DpaBanner() {
  const { user } = useAuth()
  const [needed, setNeeded] = useState(false)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!user || user.role !== 'owner') return
    let alive = true
    getDpaStatus()
      .then(({ data }) => { if (alive) setNeeded(!data.accepted) })
      .catch(() => { /* non-blocking */ })
    return () => { alive = false }
  }, [user])

  if (!user || user.role !== 'owner' || !needed) return null

  const accept = async () => {
    setBusy(true)
    try {
      await acceptDpa()
      setNeeded(false)
      setOpen(false)
    } catch {
      /* leave the banner up so they can retry */
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span>
          <strong>Action required:</strong> review and accept the Data Processing Agreement
          covering how WeTook processes your customers' data.
        </span>
        <button
          onClick={() => setOpen(true)}
          className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 font-semibold text-white hover:bg-amber-700"
        >
          Review &amp; accept
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex-1 overflow-y-auto">
              <DataProcessingAgreement />
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-5 py-3">
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
              <button
                onClick={accept}
                disabled={busy}
                className="rounded-lg bg-amber-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              >
                {busy ? 'Accepting…' : 'I accept on behalf of my organization'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
